import { Button, TextField, Typography, Stack } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import 'cordova-plugin-purchase';
import { useConfirm } from 'components/ConfirmDialog';
import { User } from 'shared/types';
import { useAuthState } from 'state/useAuthState';
import { API } from 'api';
import { enqueueSnackbar } from 'notistack';
import { Capacitor } from '@capacitor/core';

type AccountContentProps = {
  user: User;
};

export const AccountContent = (props: AccountContentProps): JSX.Element => {
  const { user } = props;
  const { logout, isOrganizationUser } = useAuthState();
  const history = useHistory();
  const mutation = useMutation({
    mutationFn: () => API.delete(`users/${user.id}/`),
  });
  const { t } = useTranslation();
  const confirm = useConfirm();

  const deleteForm = async (): Promise<void> => {
    const resp = await confirm(t('myAccount.messages.confirmDelete'));
    if (resp) {
      await mutation.mutateAsync();
      enqueueSnackbar(t('myAccount.messages.accountDeleted'), {
        variant: 'success',
      });
      history.push('/');
      await logout();
      enqueueSnackbar(t('myAccount.messages.accountDeleted'), {
        variant: 'success',
      });
      window.location.reload();
    }
  };

  const handleLogout = async (): Promise<void> => {
    const confirmationMessage = Capacitor.isNativePlatform()
      ? t('myAccount.messages.confirmLogoutMobile')
      : t('myAccount.messages.confirmLogoutWeb');
    const resp = await confirm(confirmationMessage);
    if (resp) {
      history.push('/');
      await logout();
    }
  };

  return (
    <Stack spacing={4}>
      <Stack spacing={2}>
        <Typography variant="h3">{t('myAccount.infoHeading')}</Typography>

        <TextField
          id="field-control-email"
          fullWidth
          name="email"
          type="email"
          label={t('myAccount.inputs.email.label')}
          value={user?.email || ''}
          disabled
        />

        <Button fullWidth size="large" onClick={handleLogout} color="neutral">
          {t('myAccount.buttons.logout')}
        </Button>
      </Stack>

      {!isOrganizationUser && (
        <Stack spacing={2}>
          <Typography variant="h3">{t('myAccount.delete.title')}</Typography>

          <Typography sx={{ lineHeight: '22px' }}>
            {t('myAccount.delete.description')}
          </Typography>

          <Button fullWidth size="large" onClick={deleteForm} color="error">
            {t('myAccount.buttons.deleteAccount')}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
